import { useCallback, useEffect, useSyncExternalStore } from "react";

/**
 * Manual light/dark theme. Adapted from Provider Portal's `useDarkMode`,
 * dropped to light/dark only (no system-follow) per product call — PP
 * technically supports a third "system" mode but the user-visible toggle
 * there is two-way, so we match that shape.
 *
 * The selected theme is mirrored onto `<html class="dark">`, which:
 *   • drives `dark:` tailwind variants across our chrome, and
 *   • triggers astrana-ui's own `.dark .astrana-ui` CSS-variable flip,
 *     so module internals (Claims, Labs, NBA, etc.) re-theme at the
 *     same instant as our wrapping layout.
 *
 * Value is persisted under `member-360:theme` in localStorage and kept
 * in sync across tabs via the `storage` event.
 */

type Theme = "light" | "dark";

const STORAGE_KEY = "member-360:theme";

function applyTheme(theme: Theme) {
  const root = document.documentElement;
  if (theme === "dark") root.classList.add("dark");
  else root.classList.remove("dark");
}

function readStored(): Theme {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "dark" ? "dark" : "light";
  } catch {
    return "light";
  }
}

// Module-scoped shared state so every useDarkMode() caller sees the same
// value. Initialised eagerly on module load — also used by applyThemeEarly()
// below to avoid a light→dark flash on boot.
let currentTheme: Theme = readStored();
const listeners = new Set<() => void>();

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): Theme {
  return currentTheme;
}

function setSharedTheme(next: Theme) {
  currentTheme = next;
  try {
    window.localStorage.setItem(STORAGE_KEY, next);
  } catch {
    /* storage disabled — fine, theme just won't persist */
  }
  applyTheme(next);
  listeners.forEach((l) => l());
}

/**
 * Call once from the entry bundle, before React mounts, so the class is
 * on `<html>` before any component paints. Prevents the light-mode flash
 * a dark-preference user would otherwise see during hydration.
 */
export function applyThemeEarly(): void {
  applyTheme(currentTheme);
}

export function useDarkMode() {
  const theme = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const isDark = theme === "dark";

  const setTheme = useCallback((next: Theme) => setSharedTheme(next), []);
  const toggleTheme = useCallback(
    () => setSharedTheme(currentTheme === "dark" ? "light" : "dark"),
    [],
  );

  // Cross-tab sync: when another tab writes a new value, mirror it.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      const next: Theme = e.newValue === "dark" ? "dark" : "light";
      if (next === currentTheme) return;
      currentTheme = next;
      applyTheme(next);
      listeners.forEach((l) => l());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  return { theme, isDark, setTheme, toggleTheme };
}
